import { useCallback, useEffect, useMemo, useState, type FormEvent, type ReactElement } from 'react';
import { ApiError } from '../shared/api/HttpClient';
import { healthStateLabel, incidentStateLabel, localizedProblem, severityLabel, stationModeLabel } from '../shared/i18n';
import { MonitoringApi } from './MonitoringApi';
import type { Incident, ServerSummary, StationHealth } from './types';

export function MonitoringWorkspace(): ReactElement {
  const api = useMemo(() => new MonitoringApi(), []);
  const [summary, setSummary] = useState<ServerSummary | null>(null);
  const [stations, setStations] = useState<StationHealth[]>([]);
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [includeResolved, setIncludeResolved] = useState(false);
  const [paused, setPaused] = useState(false);
  const [authRequired, setAuthRequired] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async (quiet = false): Promise<void> => {
    try {
      const [nextSummary, nextStations, nextIncidents] = await Promise.all([
        api.server(), api.stations(), api.incidents(includeResolved),
      ]);
      setSummary(nextSummary);
      setStations(nextStations);
      setIncidents(nextIncidents);
      setAuthRequired(false);
      if (!quiet) setError(null);
    } catch (reason: unknown) {
      if (reason instanceof ApiError && reason.problem.status === 401) {
        setAuthRequired(true);
        setSummary(null);
      } else if (!quiet) {
        setError(problem(reason));
      }
    }
  }, [api, includeResolved]);

  useEffect(() => { void refresh(); }, [refresh]);
  useEffect(() => {
    if (paused || authRequired) return;
    const timer = window.setInterval(() => { void refresh(true); }, 5_000);
    return () => window.clearInterval(timer);
  }, [authRequired, paused, refresh]);

  const login = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setPending('login');
    setError(null);
    try {
      await api.login(email, password);
      setPassword('');
      await refresh();
    } catch (reason: unknown) {
      setError(problem(reason));
    } finally {
      setPending(null);
    }
  };

  const action = async (incident: Incident, operation: 'acknowledge' | 'resolve' | 'restore'): Promise<void> => {
    setPending(incident.id);
    setError(null);
    try {
      if (operation === 'acknowledge') await api.acknowledge(incident.id);
      if (operation === 'resolve') await api.resolve(incident.id);
      if (operation === 'restore') await api.restoreAuto(incident.id);
      await refresh();
    } catch (reason: unknown) {
      setError(problem(reason));
    } finally {
      setPending(null);
    }
  };

  if (authRequired) {
    return <main className="monitor-login"><form onSubmit={(event) => void login(event)}><p className="eyebrow">СТАН СИСТЕМИ</p><h1>Моніторинг платформи</h1><p>Увійдіть обліковим записом із правом перегляду моніторингу.</p><label htmlFor="monitor-email">Електронна пошта</label><input id="monitor-email" type="email" autoComplete="username" required value={email} onChange={(event) => setEmail(event.target.value)} /><label htmlFor="monitor-password">Пароль</label><input id="monitor-password" type="password" autoComplete="current-password" required value={password} onChange={(event) => setPassword(event.target.value)} /><button type="submit" disabled={pending !== null}>УВІЙТИ</button>{error !== null && <p className="monitor-error" role="alert">{error}</p>}</form></main>;
  }

  return <main className="monitor-room">
    <header className="monitor-header">
      <div><p className="eyebrow">SWEETLANA · СТАН СИСТЕМИ</p><h1>Моніторинг платформи</h1></div>
      <div className="monitor-header-actions"><a className="monitor-back" href="/admin">РОЗКЛАД</a><span className={`monitor-overall state-${summary?.status.toLowerCase() ?? 'loading'}`}><i />{summary === null ? 'ЗАВАНТАЖЕННЯ' : healthStateLabel(summary.status)}</span><button type="button" onClick={() => setPaused((value) => !value)}>{paused ? 'ПРОДОВЖИТИ' : 'ПРИЗУПИНИТИ'}</button><button type="button" onClick={() => void refresh()}>ОНОВИТИ</button></div>
    </header>
    {error !== null && <div className="monitor-error-bar" role="alert">{error}<button type="button" onClick={() => setError(null)}>ЗАКРИТИ</button></div>}
    {summary === null ? <div className="workspace-loading"><span className="loading-pulse" />Завантажую стан системи…</div> : <>
      <section className="monitor-ledger" aria-label="Зведення сервера">
        <Metric label="Місткість ефіру" value={`${summary.stations.enabled}/${summary.stations.maximumActive}`} note={`${summary.stations.availableActive} вільних місць · ${summary.stations.total} створено`} danger={summary.stations.availableActive === 0} />
        <Metric label="Відкриті інциденти" value={String(summary.incidents.open)} note={`${summary.incidents.critical} критичних`} danger={summary.incidents.critical > 0} />
        <Metric label="Сховище аудіо" value={percent(summary.storage.usedBytes, summary.storage.quotaBytes)} note={`використано ${bytes(summary.storage.usedBytes)}`} />
        <Metric label="Останнє оновлення" value={time(summary.generatedAt)} note={paused ? 'оновлення призупинено' : 'кожні 5 секунд'} />
      </section>
      <section className="monitor-section">
        <header><div><p className="eyebrow">ЕФІР</p><h2>Станції</h2></div><span>усього {stations.length}</span></header>
        <div className="station-health-table" role="table" aria-label="Стан радіостанцій">
          <div className="health-row health-head" role="row"><span>Станція</span><span>Стан</span><span>Режим</span><span>Сигнал</span><span>Відгук</span><span>Черга</span><span>Інциденти</span></div>
          {stations.map((station) => <StationRow key={station.stationId} station={station} />)}
          {stations.length === 0 && <p className="monitor-empty">Станції ще не налаштовано.</p>}
        </div>
      </section>
      <section className="monitor-section incidents-section">
        <header><div><p className="eyebrow">ВІДНОВЛЕННЯ</p><h2>Інциденти</h2></div><label className="monitor-toggle"><input type="checkbox" checked={includeResolved} onChange={(event) => setIncludeResolved(event.target.checked)} /><span />Показати усунені</label></header>
        <div className="incident-ledger">
          {incidents.map((incident) => <IncidentRow key={incident.id} incident={incident} pending={pending === incident.id} onAction={action} />)}
          {incidents.length === 0 && <p className="monitor-empty">{includeResolved ? 'Інцидентів немає.' : 'Відкритих інцидентів немає.'}</p>}
        </div>
      </section>
    </>}
  </main>;
}

function Metric({ label, value, note, danger = false }: { label: string; value: string; note: string; danger?: boolean }): ReactElement {
  return <div className={danger ? 'metric-danger' : ''}><span>{label}</span><strong>{value}</strong><small>{note}</small></div>;
}

function StationRow({ station }: { station: StationHealth }): ReactElement {
  const queueMinutes = station.queueUntil === null ? null : Math.max(0, Math.round((Date.parse(station.queueUntil) - Date.now()) / 60_000));
  return <div className="health-row" role="row">
    <div><strong>{station.name}</strong><small>{station.slug}</small></div>
    <span className={`health-state state-${station.status.toLowerCase()}`}><i />{healthStateLabel(station.status)}</span>
    <code>{stationModeLabel(station.effectiveMode)}</code>
    <div className="signal-cell"><span><i style={{ width: `${Math.min(100, station.masterPeak * 100)}%` }} /></span><small>{station.audioClockAdvancing ? 'аудіосигнал рухається' : 'аудіосигнал зупинився'}</small></div>
    <code>{station.heartbeatAgeSeconds === null ? '—' : `${station.heartbeatAgeSeconds} с`}</code>
    <code>{queueMinutes === null ? '—' : `${queueMinutes} хв`}</code>
    <strong className={station.openIncidents > 0 ? 'incident-count active' : 'incident-count'}>{station.openIncidents}</strong>
  </div>;
}

function IncidentRow({ incident, pending, onAction }: { incident: Incident; pending: boolean; onAction: (incident: Incident, operation: 'acknowledge' | 'resolve' | 'restore') => Promise<void> }): ReactElement {
  const canRestore = incident.type === 'output.silence' && incident.state !== 'RESOLVED' && incident.details.guardedTakeover === true;
  return <article className={`incident-row severity-${incident.severity.toLowerCase()}`}>
    <div className="incident-time"><time>{relative(incident.openedAt)}</time><span>{severityLabel(incident.severity)}</span></div>
    <div className="incident-copy"><h3>{label(incident.type)}</h3><p>{incident.stationName ?? 'Сервер'} · {incident.service}</p><small>Останнє спостереження {time(incident.lastObservedAt)} · {incidentStateLabel(incident.state)}</small></div>
    <details><summary>ТЕХНІЧНІ ДЕТАЛІ</summary><dl>{Object.entries(incident.details).map(([key, value]) => <div key={key}><dt>{detailLabel(key)}</dt><dd>{String(value)}</dd></div>)}</dl></details>
    <div className="incident-actions">
      {incident.state === 'OPEN' && <button type="button" disabled={pending} onClick={() => void onAction(incident, 'acknowledge')}>ПІДТВЕРДИТИ</button>}
      {canRestore && <button className="primary" type="button" disabled={pending} onClick={() => void onAction(incident, 'restore')}>ВІДНОВИТИ АВТОЕФІР</button>}
      {incident.state !== 'RESOLVED' && !canRestore && <button type="button" disabled={pending} onClick={() => void onAction(incident, 'resolve')}>ПОЗНАЧИТИ УСУНЕНИМ</button>}
      {incident.state === 'RESOLVED' && <span>{incident.resolutionReason}</span>}
    </div>
  </article>;
}

function label(type: string): string { const labels: Record<string, string> = { 'output.silence': 'Тиша у вихідному сигналі', 'engine.heartbeat': 'Втрачено зв’язок з аудіорушієм', 'queue.horizon': 'Недостатня черга ефіру', 'storage.quota': 'Закінчується місце у сховищі' }; return labels[type] ?? type.replaceAll('.', ' · '); }
function detailLabel(key: string): string { return ({ guardedTakeover: 'Захищене перемикання', silenceSeconds: 'Тривалість тиші', lastSample: 'Останній зразок', reason: 'Причина' } as Record<string, string>)[key] ?? key.replaceAll('_', ' '); }
function bytes(value: number): string { return value < 1_073_741_824 ? `${(value / 1_048_576).toFixed(1)} MiB` : `${(value / 1_073_741_824).toFixed(1)} GiB`; }
function percent(used: number, quota: number): string { return quota <= 0 ? '—' : `${Math.min(100, Math.round(used / quota * 100))}%`; }
function time(value: string): string { return new Intl.DateTimeFormat('uk-UA', { hour: '2-digit', minute: '2-digit', second: '2-digit' }).format(new Date(value)); }
function relative(value: string): string { const seconds = Math.max(0, Math.round((Date.now() - Date.parse(value)) / 1000)); return seconds < 60 ? `${seconds} с тому` : `${Math.floor(seconds / 60)} хв тому`; }
function problem(reason: unknown): string { return reason instanceof ApiError ? localizedProblem(reason.problem) : reason instanceof Error ? reason.message : 'Не вдалося отримати стан моніторингу.'; }
